import { type SubmitEvent, useState } from 'react';
import { AlertCircle, Plus, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  type AddResourceTagRequest,
  useAddTagToResource,
  useRemoveTagFromResource,
  useResourceTags,
} from '@/hooks/useResourceTags';
import { useTagDefinitionsForResourceType } from '@/hooks/useTagDefinitions';
import { useTagValuesByDefinition } from '@/hooks/useTagValues';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AppSheet } from '@/components/ui/app-sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface ResourceTagsProps {
  resourceType: string;
  resourceId: string;
}

/**
 * ResourceTags — list and manage tag assignments on a resource
 * (region, division, etc.).
 *
 * Tags are key-value, where the key is a TagDefinition (with a
 * value-type of enumerated / freeform / boolean) and the value is
 * either a TagValue, a freeform string, or true (for boolean).
 *
 * Each assigned tag renders as a removable chip. Add via AppSheet
 * with three-mode value picker depending on the chosen definition.
 */
export default function ResourceTags({ resourceType, resourceId }: ResourceTagsProps) {
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedTagDefinitionId, setSelectedTagDefinitionId] = useState<string | null>(null);
  const [selectedTagValueId, setSelectedTagValueId] = useState<string>('');
  const [freeformValue, setFreeformValue] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: resourceTags, isLoading, isError, error } = useResourceTags(
    organizationId,
    resourceType,
    resourceId,
  );
  const { data: tagDefinitions } = useTagDefinitionsForResourceType(
    organizationId,
    resourceType,
  );
  const { data: tagValues } = useTagValuesByDefinition(organizationId, selectedTagDefinitionId);

  const addTagMutation = useAddTagToResource();
  const removeTagMutation = useRemoveTagFromResource();

  const resetForm = () => {
    setSelectedTagDefinitionId(null);
    setSelectedTagValueId('');
    setFreeformValue('');
    setSubmitError(null);
  };

  const handleAddTag = (e?: SubmitEvent) => {
    e?.preventDefault();
    if (!selectedTagDefinitionId) return;
    setSubmitError(null);

    const selectedDef = tagDefinitions?.find((def) => def.id === selectedTagDefinitionId);
    if (!selectedDef) return;

    const request: AddResourceTagRequest = {
      tagDefinitionId: selectedTagDefinitionId,
      tagValueId:
        selectedDef.valueType === 'enumerated' ? selectedTagValueId || null : null,
      freeformValue:
        selectedDef.valueType === 'freeform' ? freeformValue || null : null,
    };

    addTagMutation.mutate(
      { resourceType, resourceId, data: request },
      {
        onSuccess: () => {
          setIsAddOpen(false);
          resetForm();
        },
        onError: (err) => {
          setSubmitError(
            err.response?.data.description ??
              err.response?.data.error ??
              'Failed to add tag.',
          );
        },
      },
    );
  };

  const handleRemoveTag = (tagDefinitionId: string) => {
    removeTagMutation.mutate({ resourceType, resourceId, tagDefinitionId });
  };

  const selectedTagDef = tagDefinitions?.find((def) => def.id === selectedTagDefinitionId);

  if (isLoading) return <Skeleton className="h-16 w-full" />;
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Error loading tags: {error.message}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Tags</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            resetForm();
            setIsAddOpen(true);
          }}
        >
          <Plus className="w-3.5 h-3.5" />
          Add tag
        </Button>
      </div>

      {resourceTags && resourceTags.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {resourceTags.map((tag) => {
            const displayValue =
              tag.freeformValue ?? tag.tagValueDisplayName ?? tag.tagValueValue ?? 'N/A';
            return (
              <span
                key={tag.id}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-subtle px-2.5 py-1 text-sm"
              >
                <strong className="font-medium">{tag.tagDefinitionKey}:</strong>
                <span>{displayValue}</span>
                <button
                  type="button"
                  onClick={() => { handleRemoveTag(tag.tagDefinitionId); }}
                  disabled={removeTagMutation.isPending}
                  className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-accent"
                  aria-label={`Remove tag ${tag.tagDefinitionKey}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No tags assigned yet.</p>
      )}

      <AppSheet open={isAddOpen} onOpenChange={setIsAddOpen} title="Add tag">
        <AppSheet.Body>
          <form id="add-tag-form" onSubmit={handleAddTag} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="tag-def">
                Tag definition <span className="text-destructive">*</span>
              </Label>
              <Select
                value={selectedTagDefinitionId ?? ''}
                onValueChange={(v) => {
                  setSelectedTagDefinitionId(v);
                  setSelectedTagValueId('');
                  setFreeformValue('');
                }}
              >
                <SelectTrigger id="tag-def">
                  <SelectValue placeholder="Select a tag…" />
                </SelectTrigger>
                <SelectContent>
                  {tagDefinitions?.map((def) => (
                    <SelectItem key={def.id} value={def.id}>
                      {def.key} ({def.valueType})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTagDef?.valueType === 'enumerated' ? (
              <div className="space-y-1.5">
                <Label htmlFor="tag-value">
                  Value <span className="text-destructive">*</span>
                </Label>
                <Select value={selectedTagValueId} onValueChange={setSelectedTagValueId}>
                  <SelectTrigger id="tag-value">
                    <SelectValue placeholder="Select a value…" />
                  </SelectTrigger>
                  <SelectContent>
                    {tagValues?.map((val) => (
                      <SelectItem key={val.id} value={val.id}>
                        {val.displayName || val.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {selectedTagDef?.valueType === 'freeform' ? (
              <div className="space-y-1.5">
                <Label htmlFor="tag-freeform">
                  Value <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="tag-freeform"
                  type="text"
                  placeholder="Enter a value…"
                  value={freeformValue}
                  onChange={(e) => { setFreeformValue(e.target.value); }}
                  required
                />
              </div>
            ) : null}

            {selectedTagDef?.valueType === 'boolean' ? (
              <p className="text-xs text-muted-foreground">
                Boolean tags are automatically set to true when added.
              </p>
            ) : null}

            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsAddOpen(false); }}
            disabled={addTagMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" form="add-tag-form" disabled={addTagMutation.isPending}>
            {addTagMutation.isPending ? 'Adding…' : 'Add tag'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>
    </div>
  );
}
